import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ExternalLink, X, TrendingUp, BarChart3, Sparkles } from 'lucide-react'

interface SimplyStockBannerProps {
  onDismiss: () => void
  tradeCount: number
}

const SimplyStockBanner: React.FC<SimplyStockBannerProps> = ({ onDismiss, tradeCount }) => {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative mb-8 overflow-hidden"
      >
        <div className="bg-gradient-to-r from-primary-500/10 via-primary-600/10 to-primary-500/10 border border-primary-500/30 rounded-3xl p-6 backdrop-blur-sm shadow-2xl">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-3 left-6 w-12 h-12 bg-primary-400 rounded-full"></div>
            <div className="absolute top-8 right-10 w-6 h-6 bg-primary-300 rounded-full"></div>
            <div className="absolute bottom-6 left-16 w-8 h-8 bg-primary-500 rounded-full"></div>
            <div className="absolute bottom-4 right-6 w-4 h-4 bg-primary-400 rounded-full"></div>
          </div>

          <div className="relative flex items-center justify-between">
            {/* Content */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-primary-500/20 rounded-xl border border-primary-500/30">
                  <TrendingUp className="w-6 h-6 text-primary-400" />
                </div>
                <div className="p-3 bg-primary-500/20 rounded-xl border border-primary-500/30">
                  <BarChart3 className="w-6 h-6 text-primary-400" />
                </div>
                <div className="p-3 bg-primary-500/20 rounded-xl border border-primary-500/30">
                  <Sparkles className="w-6 h-6 text-primary-400" />
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-background-100 mb-2">
                  Ready to go deeper into investing?
                </h3>
                <p className="text-background-300 text-base">
                  You've made {tradeCount} trades! Explore advanced research and analysis at SimplyStock.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-4">
              <motion.a
                href="https://simplystock.net"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center space-x-3 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span>Visit SimplyStock</span>
                <ExternalLink className="w-5 h-5" />
              </motion.a>
              
              <motion.button
                onClick={onDismiss}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-3 hover:bg-background-800/50 rounded-xl transition-all duration-300 group"
                title="Dismiss banner"
              >
                <X className="w-5 h-5 text-background-400 group-hover:text-background-200" />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

export default SimplyStockBanner
